import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Select,
  Input,
  VStack,
  HStack,
  Spacer,
} from "@chakra-ui/react";
import DataTable from "react-data-table-component";
import { tableCustomStyles } from "../../themes/TableStyles";
import apiService from "../../services/ApiService";
import { useAuth } from "../../context/AuthContext";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import { autoTable } from "jspdf-autotable";
import {
  Search,
  ExportExcel,
  ExportPdf,
  Print,
  Clear,
} from "../../themes/IconButtonSet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTable } from "@fortawesome/free-solid-svg-icons";
import LivestockAmountChart from "./LivestockAmountChart";
import { StandardPageTitle, StandardHeader } from "../../themes/StandardHeader";

// ฟังก์ชันโหลดฟอนต์ Sarabun
const loadSarabunFont = async () => {
  try {
    const fontResponse = await fetch("/fonts/Sarabun-Regular.ttf");
    if (!fontResponse.ok) {
      throw new Error(
        `Failed to fetch Sarabun font: ${fontResponse.statusText}`
      );
    }
    const fontArrayBuffer = await fontResponse.arrayBuffer();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result
          .replace("data:application/octet-stream;base64,", "")
          .replace("data:font/ttf;base64,", "");
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(new Blob([fontArrayBuffer]));
    });
  } catch (error) {
    console.error("Error loading Sarabun font:", error);
    return null;
  }
};

const LivestockAmountReport = () => {
  const { sessionId } = useAuth();
  const [filterData, setFilterData] = useState({
    RecordYear: "",
    LivestockType: "",
    CountryRegion: "",
    DLDRegion: "",
    Province: "",
  });
  const [countryRegionOptions, setCountryRegionOptions] = useState([]);
  const [dldRegionOptions, setDLDRegionOptions] = useState([]);
  const [provinceOptions, setProvinceOptions] = useState([]);
  const [livestockData, setLivestockData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // ดึงข้อมูล CountryRegion List
  const fetchCountryRegionOptions = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getLivestockAmountCountryRegionList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setCountryRegionOptions(response.CountryRegionList || []);
      } else {
        console.error("Failed to fetch country regions:", response.error);
      }
    } catch (error) {
      console.error("Error fetching country regions:", error);
    }
  };

  // ดึงข้อมูล DLDRegion List
  const fetchDLDRegionOptions = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getLivestockAmountDLDRegionList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setDLDRegionOptions(response.DLDRegionList || []);
      } else {
        console.error("Failed to fetch DLD regions:", response.error);
      }
    } catch (error) {
      console.error("Error fetching DLD regions:", error);
    }
  };

  // ดึงข้อมูล Province List
  const fetchProvinceOptions = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getLivestockAmountProvinceList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setProvinceOptions(response.ProvinceList || []);
      } else {
        console.error("Failed to fetch provinces:", response.error);
      }
    } catch (error) {
      console.error("Error fetching provinces:", error);
    }
  };

  // เรียก API getLivestockAmountSummary
  const fetchLivestockReport = async () => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getLivestockAmountSummary",
        data: {
          RecordYear: filterData.RecordYear || "",
          LivestockType: filterData.LivestockType || "",
          CountryRegion: filterData.CountryRegion || "",
          DLDRegion: filterData.DLDRegion || "",
          Province: filterData.Province || "",
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setLivestockData(response.summary || []);
      } else {
        setLivestockData([]);
        alert("ไม่พบข้อมูล: " + response.error);
      }
    } catch (error) {
      console.error("Error fetching livestock report:", error);
      alert("เกิดข้อผิดพลาดในการดึงข้อมูล");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchCountryRegionOptions();
      fetchDLDRegionOptions();
      fetchProvinceOptions();
    }
  }, [sessionId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilterData({ ...filterData, [name]: value });
  };

  const columns = [
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ปี</span>
        </HStack>
      ),
      selector: (row) => row.RecordYear,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ประเภทปศุสัตว์</span>
        </HStack>
      ),
      selector: (row) => row.LivestockType,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ภูมิภาค</span>
        </HStack>
      ),
      selector: (row) => row.CountryRegion,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ปศุสัตว์เขต</span>
        </HStack>
      ),
      selector: (row) => row.DLDRegion,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>จังหวัด</span>
        </HStack>
      ),
      selector: (row) => row.Province,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>จำนวน (ตัว)</span>
        </HStack>
      ),
      selector: (row) => row.Quantity?.toLocaleString(),
      sortable: true,
    },
  ];

  const exportToExcel = () => {
    const wsData = livestockData.map((item) => ({
      ปี: item.RecordYear,
      ประเภทปศุสัตว์: item.LivestockType,
      ภูมิภาค: item.CountryRegion,
      ปศุสัตว์เขต: item.DLDRegion,
      จังหวัด: item.Province,
      "จำนวน (ตัว)": item.Quantity,
    }));
    const ws = XLSX.utils.json_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "LivestockReport");
    XLSX.writeFile(wb, "LivestockAmountReport.xlsx");
  };

  const exportToPDF = async () => {
    const doc = new jsPDF({ orientation: "landscape" });
    const fontBase64 = await loadSarabunFont();
    if (fontBase64) {
      doc.addFileToVFS("Sarabun.ttf", fontBase64);
      doc.addFont("Sarabun.ttf", "Sarabun", "normal");
      doc.setFont("Sarabun");
    } else {
      console.warn("Failed to load Sarabun font, using default font");
    }

    doc.setFontSize(16);
    doc.text("รายงานจำนวนปศุสัตว์แยกตามพื้นที่", 14, 10);
    doc.setFontSize(10);
    doc.text(`วันที่: ${new Date().toLocaleDateString("th-TH")}`, 14, 18);

    autoTable(doc, {
      startY: 25,
      head: [
        [
          "ปี",
          "ประเภทปศุสัตว์",
          "ภูมิภาค",
          "ปศุสัตว์เขต",
          "จังหวัด",
          "จำนวน (ตัว)",
        ],
      ],
      body: livestockData.map((item) => [
        item.RecordYear,
        item.LivestockType,
        item.CountryRegion,
        item.DLDRegion,
        item.Province,
        item.Quantity,
      ]),
      styles: {
        font: fontBase64 ? "Sarabun" : "helvetica",
        fontSize: 10,
        cellPadding: 2,
      },
      headStyles: {
        font: fontBase64 ? "Sarabun" : "helvetica",
        fontStyle: "normal",
        fillColor: [22, 160, 133],
        textColor: [255, 255, 255],
      },
      columnStyles: {
        0: { cellWidth: 20 },
        1: { cellWidth: 40 },
        2: { cellWidth: 40 },
        3: { cellWidth: 40 },
        4: { cellWidth: 40 },
        5: { cellWidth: 30 },
      },
      didParseCell: (data) => {
        data.cell.styles.cellWidth = "auto";
      },
    });

    doc.save("LivestockAmountReport.pdf");
  };

  const printTable = () => {
    const printContent = `
      <html>
        <head>
          <title>Livestock Amount Report</title>
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid black; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
          </style>
        </head>
        <body>
          <h2>Livestock Amount Report</h2>
          <table>
            <thead>
              <tr>
                <th>ปี</th>
                <th>ประเภทปศุสัตว์</th>
                <th>ภูมิภาค</th>
                <th>ปศุสัตว์เขต</th>
                <th>จังหวัด</th>
                <th>จำนวน (ตัว)</th>
              </tr>
            </thead>
            <tbody>
              ${livestockData
                .map(
                  (item) => `
                    <tr>
                      <td>${item.RecordYear}</td>
                      <td>${item.LivestockType}</td>
                      <td>${item.CountryRegion}</td>
                      <td>${item.DLDRegion}</td>
                      <td>${item.Province}</td>
                      <td>${item.Quantity}</td>
                    </tr>
                  `
                )
                .join("")}
            </tbody>
          </table>
        </body>
      </html>
    `;
    const newWindow = window.open("", "", "width=800,height=600");
    newWindow.document.write(printContent);
    newWindow.document.close();
    newWindow.print();
  };

  return (
    <Box p={35} m={25} borderWidth={1} borderRadius="lg" boxShadow="lg">
      <StandardPageTitle title="รายงานจำนวนปศุสัตว์แยกตามพื้นที่" />

      {/* ฟอร์มฟิลเตอร์ */}
      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
        <StandardHeader
          title="ค้นหาข้อมูลจำนวนปศุสัตว์แยกตามพื้นที่"
          bgColor="iBlue.600"
          icon={faSearch}
        />
        <VStack spacing={6} p={5}>
          <HStack spacing={6} w="100%" flexWrap="wrap">
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                ปี
              </FormLabel>
              <Input
                type="text"
                name="RecordYear"
                value={filterData.RecordYear}
                onChange={handleInputChange}
                placeholder="ระบุปี (เช่น 2024)"
                colorScheme="iBlue"
              />
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                ประเภทปศุสัตว์
              </FormLabel>
              <Input
                type="text"
                name="LivestockType"
                value={filterData.LivestockType}
                onChange={handleInputChange}
                placeholder="ระบุประเภทปศุสัตว์ (บางส่วน)"
                colorScheme="iBlue"
              />
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                ภูมิภาค
              </FormLabel>
              <Select
                name="CountryRegion"
                value={filterData.CountryRegion}
                onChange={handleInputChange}
                placeholder="เลือกภูมิภาค (ทั้งหมด)"
                colorScheme="iBlue"
              >
                {countryRegionOptions.map((region) => (
                  <option
                    key={region.CountryRegion}
                    value={region.CountryRegion}
                  >
                    {region.CountryRegion}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                ปศุสัตว์เขต
              </FormLabel>
              <Select
                name="DLDRegion"
                value={filterData.DLDRegion}
                onChange={handleInputChange}
                placeholder="เลือกปศุสัตว์เขต (ทั้งหมด)"
                colorScheme="iBlue"
              >
                {dldRegionOptions.map((dld) => (
                  <option key={dld.DLDRegion} value={dld.DLDRegion}>
                    {dld.DLDRegion}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                จังหวัด
              </FormLabel>
              <Select
                name="Province"
                value={filterData.Province}
                onChange={handleInputChange}
                placeholder="เลือกจังหวัด (ทั้งหมด)"
                colorScheme="iBlue"
              >
                {provinceOptions.map((province) => (
                  <option key={province.Province} value={province.Province}>
                    {province.Province}
                  </option>
                ))}
              </Select>
            </FormControl>
          </HStack>
          <Box w="100%" textAlign="right">
            <HStack spacing={5} justifyContent="flex-end">
              <Clear
                onClick={() =>
                  setFilterData({
                    RecordYear: "",
                    LivestockType: "",
                    CountryRegion: "",
                    DLDRegion: "",
                    Province: "",
                  })
                }
              />
              <Search onClick={fetchLivestockReport} isLoading={isLoading} />
            </HStack>
          </Box>
        </VStack>
      </Box>

      {/* ตาราง */}
      {livestockData.length > 0 && (
        <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
          <StandardHeader
            title="รายการจำนวนปศุสัตว์แยกตามพื้นที่"
            bgColor="iOrange.500"
            icon={faTable}
          />
          <HStack spacing={5} m={5}>
            <Spacer />
            <ExportExcel onClick={exportToExcel} />
            <ExportPdf onClick={exportToPDF} />
            <Print onClick={printTable} />
          </HStack>
          <Box p={5} overflowX="auto">
            <DataTable
              columns={columns}
              data={livestockData}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={tableCustomStyles}
            />
          </Box>
        </Box>
      )}

      {/* กราฟ */}
      {livestockData.length > 0 && (
        <LivestockAmountChart data={livestockData} />
      )}
    </Box>
  );
};

export default LivestockAmountReport;
